import styled from 'styled-components';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { FlexColumn } from '@elementary/common-ui/components';
import { dateRangeFromDaysBack } from '@elementary/common-ui/components/DateRangePicker';
import { DAYS_BACK } from '@elementary/common-ui/constants/urlParams';
import { safeParsePositiveInt } from '@elementary/common-ui/utils/safeTypes';
import ExecutionHistory, {
  DetailsComponentProps,
  useDefaultDateRange,
} from 'shared/components/ExecutionHistory';
import ModelFailureDetails from 'shared/components/Asset/executionInfo/ModelFailureDetails';
import PipelineExecutionInfo from 'shared/components/Pipeline/PipelineExecutionInfo';
import TestOverviewExecutionDetails from 'shared/components/TestOverviewExecutionDetails';
import { SourceType } from 'shared/types';
import type { IncidentDetail, Monitor } from '../../types';

const TabContent = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 0;
  flex: 1;
  min-height: 0;
`;

type ExecutionSource = {
  id: string;
  sourceType: SourceType;
  DetailsComponent: (props: DetailsComponentProps) => JSX.Element;
};

const PipelineExecutionDetails = ({ id }: DetailsComponentProps) => (
  <PipelineExecutionInfo data={{ rootEventId: id }} />
);

export const getMonitorExecutionSource = (
  monitor: Monitor | undefined,
): ExecutionSource | null => {
  if (!monitor) {
    return null;
  }

  const subTestUniqueId = monitor.latestTestExecution?.subTestUniqueId;
  if (monitor.test && subTestUniqueId) {
    return {
      id: subTestUniqueId,
      sourceType: SourceType.TEST,
      DetailsComponent: TestOverviewExecutionDetails,
    };
  }

  const assetId = monitor.asset?.id;
  if (!assetId) {
    return null;
  }

  if (monitor.asset?.type === 'ETL') {
    return {
      id: assetId,
      sourceType: SourceType.PIPELINE,
      DetailsComponent: PipelineExecutionDetails,
    };
  }

  return {
    id: assetId,
    sourceType: SourceType.MODEL,
    DetailsComponent: ModelFailureDetails,
  };
};

type Props = {
  incident: IncidentDetail;
};

const IncidentExecutionHistoryTab = ({ incident }: Props) => {
  const [searchParams] = useSearchParams();
  const defaultDaysBack = useDefaultDateRange();

  const daysBackParam = searchParams.get(DAYS_BACK);
  const daysBack = safeParsePositiveInt(daysBackParam, defaultDaysBack);
  const dateRange = dateRangeFromDaysBack(daysBack);

  const executionSource = getMonitorExecutionSource(incident.monitors[0]);
  if (!executionSource) {
    return null;
  }

  const { id, sourceType, DetailsComponent } = executionSource;

  return (
    <TabContent>
      <ExecutionHistory
        DetailsComponent={DetailsComponent}
        id={id}
        sourceType={sourceType}
        dateRange={dateRange}
      />
    </TabContent>
  );
};

export default IncidentExecutionHistoryTab;
